import type { ServicePageProps } from "@/components/ServicePage";

export interface AutomacaoPage {
  slug: string;
  navLabel: string;
  navDescription: string;
  seoTitle: string;
  seoDescription: string;
  page: ServicePageProps;
}

const processoPadrao = [
  { n: "01", t: "Diagnóstico", d: "Mapeamos canais, volume de contatos, funil atual e onde as oportunidades se perdem." },
  { n: "02", t: "Desenho", d: "Redesenhamos o processo comercial com etapas, responsáveis e gatilhos claros." },
  { n: "03", t: "Automação", d: "Implantamos IA, fluxos, integrações e cadências dentro das ferramentas certas." },
  { n: "04", t: "Treinamento", d: "Capacitamos a equipe para operar, ajustar e extrair o máximo do processo." },
  { n: "05", t: "Otimização", d: "Acompanhamos indicadores e refinamos o fluxo mês a mês." },
];

function build(p: AutomacaoPage): AutomacaoPage {
  return { ...p, page: { processSteps: processoPadrao, ...p.page } };
}

const list: AutomacaoPage[] = [
  build({
    slug: "automacao-comercial-para-empresas",
    navLabel: "Automação comercial para empresas",
    navDescription: "Processos, automação e IA para atender mais e vender melhor",
    seoTitle: "Automação Comercial para Empresas | SVG Multimídia",
    seoDescription:
      "Transforme sua operação de atendimento e vendas com processos inteligentes, automação e IA. Reduza tarefas, acompanhe mais oportunidades e aumente a eficiência comercial.",
    page: {
      eyebrow: "Automação Comercial",
      breadcrumb: "Automação comercial para empresas",
      h1: "Automação comercial para empresas que precisam atender mais, vender melhor e crescer com eficiência",
      heroDescription:
        "Unimos processo, tecnologia e inteligência artificial para que sua empresa responda mais rápido, acompanhe todas as oportunidades e venda com previsibilidade.",
      problemTitle: "O gargalo raramente é falta de leads — é falta de processo",
      problemText:
        "Mensagens sem resposta, follow-up esquecido, informações espalhadas em planilhas e conversas pessoais. Sem um processo comercial automatizado, o crescimento depende do esforço heroico da equipe e trava exatamente quando a demanda aumenta.",
      whatTitle: "O que entra em um projeto de automação comercial",
      whatItems: [
        { k: "funnel", c: "Funil", t: "Funil comercial estruturado", d: "Etapas, critérios de avanço e responsáveis definidos para cada oportunidade." },
        { k: "ai-wa", c: "IA", t: "Atendimento com IA", d: "Assistente treinado no seu negócio para responder, qualificar e encaminhar contatos." },
        { k: "followup", c: "Funil", t: "Cadências de follow-up", d: "Retornos automáticos programados para que nenhum lead esfrie por esquecimento." },
        { k: "api", c: "Integrações", t: "Integrações", d: "WhatsApp, site, formulários, anúncios e CRM conversando entre si." },
        { k: "dashboard", c: "Gestão", t: "Painel de indicadores", d: "Tempo de resposta, taxa de conversão e oportunidades paradas em tempo real." },
        { k: "agents", c: "Atendimento", t: "Distribuição por equipe", d: "Filas, rodízio e regras de atendimento que evitam contatos sem dono." },
      ],
      benefits: [
        "Resposta em segundos, 24 horas por dia",
        "Nenhum contato perdido ou esquecido",
        "Equipe focada em vender, não em tarefas manuais",
        "Histórico completo de cada oportunidade",
        "Previsibilidade de faturamento",
        "Escala sem aumentar proporcionalmente o time",
      ],
      keywords: ["automação comercial", "automação de vendas", "CRM com IA", "processos comerciais"],
      serviceName: "Automação Comercial para Empresas",
      serviceDescription:
        "Projeto completo de automação comercial: diagnóstico, desenho de processos, implantação de CRM com inteligência artificial, integrações com WhatsApp e canais digitais, cadências de follow-up e painéis de indicadores.",
      introParagraphs: [
        "Automação comercial não é trocar pessoas por robôs. É eliminar o trabalho repetitivo que consome o tempo do seu time — copiar dados, lembrar de retornar, procurar histórico de conversa — para que a energia da equipe fique onde ela realmente gera receita: no relacionamento e no fechamento.",
        "Na SVG Multimídia, todo projeto começa pelo processo, não pela ferramenta. Mapeamos como o contato chega, quem responde, em quanto tempo, o que acontece quando o cliente não responde e onde as oportunidades morrem. Só depois desenhamos a automação: IA de atendimento, cadências, integrações e painéis.",
        "O resultado é uma operação comercial que funciona igual todos os dias, independentemente de quem está de férias, do volume de mensagens ou do horário em que o cliente decidiu chamar.",
      ],
      metrics: [
        { value: "24h", label: "de atendimento contínuo com IA" },
        { value: "-70%", label: "de tarefas manuais na rotina comercial" },
        { value: "<1min", label: "tempo médio de primeira resposta" },
        { value: "100%", label: "das oportunidades registradas" },
      ],
      differentials: [
        { t: "Processo antes da ferramenta", d: "Automatizar um processo ruim só acelera o problema. Começamos pelo desenho do fluxo comercial." },
        { t: "IA treinada no seu negócio", d: "A inteligência artificial responde com o seu tom, seus produtos, preços e regras — não com respostas genéricas." },
        { t: "Implantação assistida", d: "Não entregamos um acesso e desejamos boa sorte: configuramos, treinamos e acompanhamos a adoção." },
        { t: "Integração com marketing", d: "Somos também uma agência de SEO: o que geramos de demanda entra direto no funil automatizado." },
        { t: "Indicadores reais", d: "Medimos tempo de resposta, taxa de conversão e receita — não métricas de vaidade." },
        { t: "Suporte contínuo", d: "A operação muda; a automação acompanha com revisões e ajustes periódicos." },
      ],
      forWho: [
        "Empresas que recebem mais contatos do que conseguem responder",
        "Times comerciais que perdem leads por falta de follow-up",
        "Negócios que controlam vendas em planilha ou no caderno",
        "Operações com múltiplos atendentes e sem padrão",
        "Empresas em crescimento que precisam escalar sem inchar o time",
        "Gestores sem visibilidade do que acontece no atendimento",
      ],
      deliverables: [
        "Diagnóstico completo do processo comercial atual",
        "Desenho do novo funil com etapas e responsáveis",
        "Implantação de CRM com inteligência artificial",
        "Integração com WhatsApp, site e formulários",
        "Cadências automáticas de follow-up",
        "Scripts e respostas padronizadas",
        "Painel de indicadores comerciais",
        "Treinamento da equipe",
        "Acompanhamento e otimização mensal",
      ],
      faq: [
        { q: "Automação substitui minha equipe?", a: "Não. Ela elimina o trabalho repetitivo e devolve tempo para o time se dedicar ao relacionamento e ao fechamento." },
        { q: "Preciso trocar de sistema?", a: "Nem sempre. Avaliamos suas ferramentas atuais e só recomendamos mudança quando ela realmente traz ganho." },
        { q: "Quanto tempo leva a implantação?", a: "Projetos típicos ficam operacionais entre 2 e 6 semanas, dependendo da complexidade e do número de canais." },
        { q: "Funciona para empresas pequenas?", a: "Sim. O escopo é dimensionado ao tamanho da operação — pequenas empresas costumam ver ganho imediato em tempo de resposta." },
        { q: "Como a IA responde meus clientes?", a: "Ela é treinada com suas informações, produtos, políticas e tom de voz, e transfere para um humano sempre que necessário." },
        { q: "Consigo medir o retorno?", a: "Sim. Acompanhamos tempo de resposta, oportunidades trabalhadas e conversão antes e depois da automação." },
      ],
    },
  }),
  build({
    slug: "automacao-de-atendimento-ao-cliente",
    navLabel: "Automação de atendimento ao cliente",
    navDescription: "Respostas rápidas, triagem automática e zero contato perdido",
    seoTitle: "Automação de Atendimento ao Cliente | SVG Multimídia",
    seoDescription:
      "Automatize o atendimento da sua empresa com IA, triagem inteligente e respostas imediatas. Atenda 24 horas, reduza filas e melhore a experiência do cliente.",
    page: {
      eyebrow: "Atendimento",
      breadcrumb: "Automação de atendimento ao cliente",
      h1: "Automação de atendimento ao cliente para empresas que não podem deixar ninguém esperando",
      heroDescription:
        "Triagem automática, respostas imediatas e IA que resolve o simples e encaminha o complexo para a pessoa certa.",
      problemTitle: "Cliente que espera muito tempo procura o concorrente",
      problemText:
        "A maior parte das perdas de venda acontece antes de qualquer negociação: o cliente manda mensagem, ninguém responde a tempo e ele resolve o problema em outro lugar. Atendimento lento é perda de receita silenciosa.",
      whatTitle: "O que automatizamos no atendimento",
      whatItems: [
        { k: "chats", c: "Atendimento", t: "Resposta imediata", d: "Toda mensagem recebe retorno em segundos, em qualquer horário." },
        { k: "tags", c: "Atendimento", t: "Triagem inteligente", d: "A IA identifica o assunto e encaminha para o setor ou atendente correto." },
        { k: "qa", c: "IA", t: "Base de respostas", d: "Dúvidas frequentes respondidas automaticamente com informação sempre atualizada." },
        { k: "notify", c: "Gestão", t: "Alertas de espera", d: "Notificações quando um contato passa do tempo máximo sem resposta." },
        { k: "multi", c: "Integrações", t: "Canais unificados", d: "WhatsApp, Instagram, site e e-mail em uma única caixa de entrada." },
        { k: "metrics", c: "Resultado", t: "Indicadores de serviço", d: "Tempo de resposta, volume por canal, assuntos mais frequentes e satisfação." },
      ],
      benefits: [
        "Atendimento 24 horas sem plantão",
        "Fila de espera praticamente eliminada",
        "Padronização da informação passada ao cliente",
        "Menos retrabalho e menos conversas repetidas",
        "Equipe focada em casos que exigem gente",
        "Experiência do cliente mais consistente",
      ],
      keywords: ["automação de atendimento", "chatbot com IA", "atendimento 24 horas", "SAC automatizado"],
      serviceName: "Automação de Atendimento ao Cliente",
      serviceDescription:
        "Implantação de atendimento automatizado com inteligência artificial, triagem por assunto, unificação de canais, base de respostas e indicadores de nível de serviço.",
      introParagraphs: [
        "Automatizar atendimento é organizar a porta de entrada da sua empresa. O primeiro contato precisa acontecer rápido, com informação correta e com um caminho claro para o próximo passo — seja um orçamento, um agendamento ou um suporte.",
        "Trabalhamos com uma camada de IA que entende a intenção da mensagem, responde o que é simples e transfere para o humano o que exige análise. Tudo registrado, com histórico completo e sem depender de quem está online no momento.",
      ],
      metrics: [
        { value: "24/7", label: "disponibilidade real de atendimento" },
        { value: "-80%", label: "de dúvidas repetitivas no time" },
        { value: "seg", label: "tempo de primeira resposta" },
        { value: "1", label: "caixa de entrada para todos os canais" },
      ],
      differentials: [
        { t: "IA que sabe quando parar", d: "Regras claras de transferência para humano evitam a frustração típica de chatbots engessados." },
        { t: "Conteúdo do seu negócio", d: "A base de respostas é construída com suas informações reais, revisada junto com sua equipe." },
        { t: "Multicanal de verdade", d: "O histórico do cliente é único, independentemente do canal que ele usou." },
        { t: "Foco em experiência", d: "Automação com linguagem humana, sem menus intermináveis." },
      ],
      forWho: [
        "Empresas com alto volume de dúvidas repetidas",
        "Negócios que atendem fora do horário comercial",
        "Times pequenos sobrecarregados de mensagens",
        "Clínicas, escolas, comércios e prestadores de serviço",
        "Operações com vários canais desconectados",
      ],
      deliverables: [
        "Mapeamento das dúvidas e fluxos de atendimento",
        "Configuração da IA de atendimento",
        "Unificação dos canais em uma única plataforma",
        "Regras de triagem e transferência",
        "Base de respostas revisada",
        "Painel de indicadores de atendimento",
        "Treinamento da equipe",
      ],
      faq: [
        { q: "O cliente percebe que é uma IA?", a: "A comunicação é transparente e natural. Quando o assunto exige, a transferência para um atendente humano é imediata." },
        { q: "Funciona no WhatsApp oficial?", a: "Sim, tanto com API oficial quanto com conexões alternativas, conforme o perfil da operação." },
        { q: "E se a IA não souber responder?", a: "Ela encaminha para o setor correto e registra a lacuna para atualizarmos a base." },
        { q: "Consigo manter o atendimento humano?", a: "Sim. A automação cuida da triagem e do repetitivo; sua equipe segue no que importa." },
      ],
    },
  }),
  build({
    slug: "consultoria-de-automacao-comercial",
    navLabel: "Consultoria de automação comercial",
    navDescription: "Diagnóstico, desenho de processos e plano de automação sob medida",
    seoTitle: "Consultoria de Automação Comercial | SVG Multimídia",
    seoDescription:
      "Consultoria especializada em automação comercial: diagnóstico da operação, redesenho de processos, escolha de ferramentas e plano de implantação com indicadores.",
    page: {
      eyebrow: "Consultoria",
      breadcrumb: "Consultoria de automação comercial",
      h1: "Consultoria de automação comercial para empresas que querem crescer com processo, não com improviso",
      heroDescription:
        "Análise profunda da sua operação de atendimento e vendas, com plano prático de automação, ferramentas recomendadas e indicadores de acompanhamento.",
      problemTitle: "Ferramenta sem processo vira mais um custo parado",
      problemText:
        "Muitas empresas compram CRM, chatbot e automação e continuam com o mesmo resultado. O motivo é simples: nenhuma ferramenta corrige um processo comercial que nunca foi desenhado.",
      whatTitle: "O que a consultoria entrega",
      whatItems: [
        { k: "analysis", c: "Estratégia", t: "Diagnóstico da operação", d: "Entrevistas, análise de conversas e mapeamento do funil real da empresa." },
        { k: "funnel", c: "Funil", t: "Redesenho de processo", d: "Novo fluxo comercial com etapas, gatilhos, SLAs e responsáveis." },
        { k: "api", c: "Integrações", t: "Escolha de ferramentas", d: "Recomendação técnica e imparcial das plataformas adequadas ao seu porte." },
        { k: "dashboard", c: "Gestão", t: "Modelo de indicadores", d: "Definição de métricas, metas e rotina de acompanhamento gerencial." },
        { k: "agents", c: "Gestão", t: "Estrutura de equipe", d: "Papéis, filas e regras de distribuição de oportunidades." },
        { k: "metrics", c: "Resultado", t: "Roadmap de implantação", d: "Plano por fases, com prioridades, prazos e responsáveis." },
      ],
      benefits: [
        "Clareza sobre onde o dinheiro está sendo perdido",
        "Processo documentado e replicável",
        "Decisão de ferramenta baseada em necessidade real",
        "Roadmap com prioridades e prazos",
        "Redução de retrabalho e custo operacional",
        "Base sólida para escalar o time comercial",
      ],
      keywords: ["consultoria de automação comercial", "consultoria de vendas", "processos comerciais", "diagnóstico comercial"],
      serviceName: "Consultoria de Automação Comercial",
      serviceDescription:
        "Consultoria que mapeia a operação comercial, redesenha processos, define indicadores, recomenda ferramentas e entrega um roadmap de automação por fases.",
      introParagraphs: [
        "A consultoria existe para responder três perguntas: onde sua empresa perde oportunidades, o que deve ser automatizado primeiro e qual o retorno esperado de cada mudança.",
        "Trabalhamos com dados da sua própria operação — conversas reais, tempos de resposta, volume por canal e taxa de conversão por etapa — para propor mudanças que cabem na sua realidade e no seu orçamento.",
      ],
      metrics: [
        { value: "360°", label: "análise de canais, funil e equipe" },
        { value: "Fases", label: "roadmap priorizado por impacto" },
        { value: "KPIs", label: "indicadores definidos e monitoráveis" },
        { value: "0", label: "vínculo comercial com fornecedores" },
      ],
      differentials: [
        { t: "Visão de marketing e vendas", d: "Analisamos também a origem da demanda: SEO, anúncios e canais orgânicos." },
        { t: "Recomendação imparcial", d: "Indicamos a ferramenta certa para o seu caso, inclusive quando a resposta é manter a atual." },
        { t: "Entrega documentada", d: "Você fica com o processo desenhado, mesmo que a implantação seja feita internamente." },
        { t: "Foco em execução", d: "O plano vem com prioridades e responsáveis, não com teoria." },
      ],
      forWho: [
        "Empresas que investiram em ferramentas e não viram resultado",
        "Gestores sem visibilidade do funil comercial",
        "Operações que vão crescer e precisam de estrutura antes",
        "Times comerciais com processos informais",
        "Negócios em fase de profissionalização da gestão",
      ],
      deliverables: [
        "Relatório de diagnóstico da operação",
        "Mapa do funil atual e do funil proposto",
        "Definição de SLAs e indicadores",
        "Recomendação de ferramentas e integrações",
        "Roadmap de implantação por fases",
        "Sessão de apresentação e alinhamento com a equipe",
      ],
      faq: [
        { q: "A consultoria inclui a implantação?", a: "Pode incluir. A consultoria entrega o plano; a implantação é um escopo complementar que podemos executar." },
        { q: "Quanto tempo dura?", a: "O diagnóstico completo costuma levar de 2 a 4 semanas, dependendo do tamanho da operação." },
        { q: "Preciso parar a operação?", a: "Não. Todo o trabalho é feito em paralelo à rotina, com entrevistas curtas e análise de dados existentes." },
        { q: "Serve para empresas de qualquer setor?", a: "Sim, desde que exista uma operação de atendimento e vendas recorrente." },
      ],
    },
  }),
  build({
    slug: "automacao-de-vendas-pelo-whatsapp",
    navLabel: "Automação de vendas pelo WhatsApp",
    navDescription: "Funil, follow-up e IA no canal onde seu cliente já está",
    seoTitle: "Automação de Vendas pelo WhatsApp | SVG Multimídia",
    seoDescription:
      "Estruture um funil de vendas completo dentro do WhatsApp: atendimento com IA, qualificação automática, follow-up programado e controle total das oportunidades.",
    page: {
      eyebrow: "WhatsApp",
      breadcrumb: "Automação de vendas pelo WhatsApp",
      h1: "Automação de vendas pelo WhatsApp para transformar conversas em oportunidades controladas",
      heroDescription:
        "Do primeiro 'oi' ao fechamento: qualificação com IA, etapas de funil, cadências de retorno e histórico completo dentro do canal preferido do brasileiro.",
      problemTitle: "O WhatsApp virou o principal canal de vendas — e o mais bagunçado",
      problemText:
        "Conversas espalhadas em celulares pessoais, sem histórico, sem etapa e sem previsibilidade. Quando um vendedor sai, a carteira vai junto. Quando o volume cresce, as mensagens somem no meio da lista.",
      whatTitle: "Como estruturamos o WhatsApp comercial",
      whatItems: [
        { k: "ai-wa", c: "IA", t: "Qualificação com IA", d: "A IA faz as perguntas certas e classifica o lead antes de chegar no vendedor." },
        { k: "funnel", c: "Funil", t: "Funil dentro do chat", d: "Cada conversa vira uma oportunidade com etapa, valor e responsável." },
        { k: "followup", c: "Funil", t: "Follow-up automático", d: "Sequências de retorno para quem não respondeu ou pediu para falar depois." },
        { k: "agents", c: "Atendimento", t: "Múltiplos atendentes", d: "Um número, vários operadores, com distribuição e histórico compartilhado." },
        { k: "broadcast", c: "Integrações", t: "Campanhas segmentadas", d: "Envios para bases segmentadas por etapa, interesse ou origem." },
        { k: "dashboard", c: "Gestão", t: "Gestão da carteira", d: "Visão de oportunidades abertas, paradas e ganhas por vendedor." },
      ],
      benefits: [
        "Fim das conversas perdidas em celulares pessoais",
        "Leads qualificados antes de chegar ao vendedor",
        "Follow-up acontecendo sem depender de memória",
        "Carteira de clientes é da empresa, não do vendedor",
        "Previsão de vendas com base em dados reais",
        "Mais conversões com o mesmo volume de contatos",
      ],
      keywords: ["automação WhatsApp", "CRM WhatsApp", "vendas pelo WhatsApp", "funil de vendas WhatsApp"],
      serviceName: "Automação de Vendas pelo WhatsApp",
      serviceDescription:
        "Estruturação completa do WhatsApp como canal de vendas: CRM integrado, IA de qualificação, funil por etapas, cadências de follow-up, campanhas segmentadas e relatórios.",
      introParagraphs: [
        "No Brasil, a decisão de compra passa pelo WhatsApp. O problema é que o aplicativo, sozinho, não foi feito para gerir um processo comercial: não tem etapa, não tem lembrete, não tem histórico consolidado nem indicador.",
        "Nossa implantação conecta o WhatsApp a um CRM com inteligência artificial. A conversa continua natural para o cliente, mas por trás existe um funil organizado, com qualificação automática, lembretes de retorno e visão gerencial de toda a carteira.",
      ],
      metrics: [
        { value: "1 nº", label: "para toda a equipe comercial" },
        { value: "24h", label: "de qualificação automática" },
        { value: "+3x", label: "de contatos trabalhados por vendedor" },
        { value: "0", label: "oportunidades sem registro" },
      ],
      differentials: [
        { t: "API oficial e não oficial", d: "Trabalhamos com as duas modalidades de conexão, conforme a necessidade e o porte da operação." },
        { t: "IA com contexto de vendas", d: "A IA não só responde: ela qualifica, agenda e prepara o terreno para o vendedor." },
        { t: "Migração sem perder histórico", d: "Organizamos a transição dos números atuais para a operação centralizada." },
        { t: "Treinamento prático", d: "Sua equipe aprende a operar o funil no dia a dia, com material de apoio." },
      ],
      forWho: [
        "Empresas que vendem majoritariamente pelo WhatsApp",
        "Times com vários vendedores usando números próprios",
        "Negócios com ciclo de venda que exige follow-up",
        "Operações que recebem leads de anúncios e site",
        "Gestores que não conseguem auditar as conversas",
      ],
      deliverables: [
        "Conexão do WhatsApp ao CRM",
        "Configuração da IA de qualificação",
        "Funil de vendas com etapas personalizadas",
        "Cadências de follow-up automáticas",
        "Regras de distribuição entre vendedores",
        "Modelos de mensagem e campanhas segmentadas",
        "Painel gerencial e treinamento da equipe",
      ],
      faq: [
        { q: "Posso manter meu número atual?", a: "Sim, na maioria dos casos o número existente é migrado para a plataforma." },
        { q: "Vou perder o histórico das conversas?", a: "As conversas antigas do aparelho permanecem; a partir da implantação todo o histórico passa a ficar centralizado." },
        { q: "Corro risco de bloqueio?", a: "Trabalhamos com boas práticas de envio e, quando o volume exige, com a API oficial do WhatsApp." },
        { q: "Funciona com poucos vendedores?", a: "Sim. Mesmo operações de uma pessoa ganham com a organização e o follow-up automático." },
      ],
    },
  }),
  build({
    slug: "inteligencia-artificial-para-atendimento-empresarial",
    navLabel: "IA para atendimento empresarial",
    navDescription: "IA treinada no seu negócio atendendo 24 horas por dia",
    seoTitle: "Inteligência Artificial para Atendimento Empresarial | SVG Multimídia",
    seoDescription:
      "Implante uma IA treinada no seu negócio para atender, qualificar e encaminhar contatos 24 horas por dia, com linguagem natural e transferência humana quando necessário.",
    page: {
      eyebrow: "Inteligência Artificial",
      breadcrumb: "IA para atendimento empresarial",
      h1: "Inteligência artificial para atendimento empresarial que responde como sua melhor atendente",
      heroDescription:
        "Uma IA treinada com os produtos, políticas e o tom de voz da sua empresa, disponível todos os dias, em todos os horários.",
      problemTitle: "Chatbot genérico irrita; IA bem treinada vende",
      problemText:
        "Menus rígidos e respostas prontas frustram o cliente e prejudicam a marca. O que resolve é uma inteligência artificial que entende a pergunta real, responde com informação correta e sabe a hora de chamar uma pessoa.",
      whatTitle: "Como implantamos a IA de atendimento",
      whatItems: [
        { k: "qa", c: "IA", t: "Treinamento com sua base", d: "Produtos, preços, políticas, prazos e perguntas frequentes alimentam o modelo." },
        { k: "ai-wa", c: "IA", t: "Linguagem natural", d: "Conversas fluidas, sem menus numerados e sem respostas robóticas." },
        { k: "lead", c: "Funil", t: "Qualificação automática", d: "A IA coleta as informações que o time precisa antes de assumir o contato." },
        { k: "schedule", c: "Atendimento", t: "Agendamentos", d: "Marcação de visitas, reuniões e atendimentos direto na conversa." },
        { k: "audio", c: "Atendimento", t: "Áudio e mídia", d: "Interpretação de áudios e recebimento de documentos e imagens." },
        { k: "analysis", c: "Gestão", t: "Aprendizado contínuo", d: "Revisões periódicas com base nas conversas reais para melhorar as respostas." },
      ],
      benefits: [
        "Atendimento imediato em qualquer horário",
        "Informação padronizada e sempre correta",
        "Redução drástica de perguntas repetitivas",
        "Leads chegam qualificados ao time",
        "Escala sem contratar mais atendentes",
        "Melhor experiência e mais confiança do cliente",
      ],
      keywords: ["IA para atendimento", "inteligência artificial empresarial", "agente de IA", "chatbot inteligente"],
      serviceName: "Inteligência Artificial para Atendimento Empresarial",
      serviceDescription:
        "Implantação de agente de inteligência artificial treinado no negócio do cliente para atendimento, qualificação, agendamento e encaminhamento humano em múltiplos canais.",
      introParagraphs: [
        "A diferença entre um chatbot e um agente de IA está no treinamento. Um chatbot segue um roteiro fixo; um agente de IA compreende a intenção, consulta a base de conhecimento da sua empresa e formula a resposta adequada — inclusive para perguntas que ninguém previu.",
        "Nosso processo envolve levantar todo o conhecimento operacional da empresa, estruturar essa base, definir limites claros (o que a IA pode e não pode dizer) e configurar as regras de transferência para atendimento humano.",
      ],
      metrics: [
        { value: "24/7", label: "atendimento sem interrupção" },
        { value: "-80%", label: "de perguntas repetitivas no time" },
        { value: "seg", label: "tempo de resposta médio" },
        { value: "∞", label: "conversas simultâneas" },
      ],
      differentials: [
        { t: "Base de conhecimento estruturada", d: "Organizamos a informação da empresa antes de treinar a IA — é isso que garante respostas corretas." },
        { t: "Limites bem definidos", d: "A IA sabe o que não deve responder e quando transferir, evitando promessas indevidas." },
        { t: "Revisão contínua", d: "Auditamos conversas reais periodicamente e refinamos o comportamento do agente." },
        { t: "Integração com o funil", d: "O que a IA coleta alimenta diretamente o CRM e as etapas de venda." },
      ],
      forWho: [
        "Empresas com alto volume de contatos",
        "Negócios que perdem vendas fora do horário comercial",
        "Operações com informações complexas de produto",
        "Equipes sobrecarregadas com dúvidas básicas",
        "Empresas que querem padronizar o discurso comercial",
      ],
      deliverables: [
        "Levantamento e estruturação da base de conhecimento",
        "Configuração e treinamento do agente de IA",
        "Definição de regras de transferência humana",
        "Integração com WhatsApp e demais canais",
        "Fluxos de qualificação e agendamento",
        "Rodadas de teste e ajuste fino",
        "Revisões periódicas de qualidade",
      ],
      faq: [
        { q: "A IA pode dar informação errada?", a: "Ela responde a partir de uma base controlada por você, com limites definidos e transferência humana em casos sensíveis." },
        { q: "Preciso de conhecimento técnico?", a: "Não. Toda a configuração é feita por nós e sua equipe recebe treinamento de operação." },
        { q: "A IA aprende sozinha?", a: "Ela usa a base que você controla. As melhorias são feitas em revisões periódicas, com curadoria." },
        { q: "É possível manter o tom da minha marca?", a: "Sim. Definimos o tom de voz, vocabulário e nível de formalidade do agente." },
      ],
    },
  }),
  build({
    slug: "estruturacao-de-processos-comerciais",
    navLabel: "Estruturação de processos comerciais",
    navDescription: "Etapas claras, responsáveis definidos e previsibilidade",
    seoTitle: "Estruturação de Processos Comerciais | SVG Multimídia",
    seoDescription:
      "Organize seu processo de vendas com etapas claras, SLAs, scripts e indicadores. Estruturação comercial para empresas que querem previsibilidade e escala.",
    page: {
      eyebrow: "Processos",
      breadcrumb: "Estruturação de processos comerciais",
      h1: "Estruturação de processos comerciais para vender com previsibilidade, não com sorte",
      heroDescription:
        "Do primeiro contato ao pós-venda: etapas definidas, responsáveis claros, scripts validados e indicadores que mostram onde ajustar.",
      problemTitle: "Sem processo, o resultado depende do dia e da pessoa",
      problemText:
        "Cada vendedor atende de um jeito, cada lead segue um caminho diferente e ninguém sabe explicar por que um mês vende o dobro do outro. Isso não é falta de talento — é falta de processo documentado.",
      whatTitle: "O que estruturamos",
      whatItems: [
        { k: "funnel", c: "Funil", t: "Etapas do funil", d: "Definição de cada estágio, critério de entrada e critério de avanço." },
        { k: "tags", c: "Gestão", t: "SLAs de atendimento", d: "Tempo máximo de resposta e de retorno em cada etapa do processo." },
        { k: "qa", c: "Atendimento", t: "Scripts e abordagens", d: "Roteiros de qualificação, objeções e fechamento validados com o time." },
        { k: "agents", c: "Gestão", t: "Papéis e responsabilidades", d: "Quem prospecta, quem qualifica, quem fecha e quem faz o pós-venda." },
        { k: "metrics", c: "Resultado", t: "Indicadores por etapa", d: "Conversão, tempo médio e volume em cada estágio do funil." },
        { k: "dashboard", c: "Gestão", t: "Rotina de gestão", d: "Reuniões, painéis e rituais de acompanhamento comercial." },
      ],
      benefits: [
        "Previsibilidade de vendas mês a mês",
        "Onboarding rápido de novos vendedores",
        "Padrão de qualidade no atendimento",
        "Identificação clara dos gargalos",
        "Menos dependência de pessoas-chave",
        "Base pronta para automatizar",
      ],
      keywords: ["processos comerciais", "estruturação de vendas", "funil de vendas", "playbook comercial"],
      serviceName: "Estruturação de Processos Comerciais",
      serviceDescription:
        "Desenho e documentação do processo comercial: etapas de funil, SLAs, scripts, papéis, indicadores e rotina de gestão, preparando a operação para a automação.",
      introParagraphs: [
        "Automação é a consequência de um processo bem definido. Antes de configurar qualquer ferramenta, é preciso responder: quais são as etapas da nossa venda, o que precisa acontecer para um lead avançar e quem é responsável por cada movimento.",
        "A estruturação transforma o conhecimento que hoje está na cabeça das pessoas em um playbook comercial: documentado, treinável e mensurável. É o que permite crescer sem perder qualidade.",
      ],
      metrics: [
        { value: "Playbook", label: "processo documentado e treinável" },
        { value: "SLAs", label: "tempos de resposta definidos" },
        { value: "KPIs", label: "indicadores por etapa do funil" },
        { value: "Escala", label: "base pronta para automação" },
      ],
      differentials: [
        { t: "Construído com a equipe", d: "O processo nasce de workshops com quem vende, o que garante adoção real." },
        { t: "Pronto para automatizar", d: "O desenho já considera onde a IA e as automações vão atuar." },
        { t: "Documentação prática", d: "Material objetivo, usado no dia a dia e no treinamento de novos vendedores." },
      ],
      forWho: [
        "Empresas que vão contratar novos vendedores",
        "Operações com resultados inconstantes",
        "Negócios que dependem do dono para vender",
        "Times sem clareza sobre etapas e responsabilidades",
        "Empresas que vão implantar CRM",
      ],
      deliverables: [
        "Mapa do processo comercial atual",
        "Desenho do funil e critérios de avanço",
        "Definição de SLAs e responsabilidades",
        "Scripts de qualificação e fechamento",
        "Playbook comercial documentado",
        "Painel de indicadores por etapa",
        "Workshop de implantação com a equipe",
      ],
      faq: [
        { q: "Isso não engessa o vendedor?", a: "Ao contrário: o processo dá segurança e libera o vendedor para focar no relacionamento, não em decidir o próximo passo." },
        { q: "Preciso de CRM antes?", a: "Não. A estruturação pode acontecer antes e orienta qual ferramenta faz sentido." },
        { q: "Serve para venda consultiva longa?", a: "Sim. O desenho é adaptado ao ciclo de venda e à complexidade do seu produto ou serviço." },
      ],
    },
  }),
  build({
    slug: "automacao-de-follow-up",
    navLabel: "Automação de follow-up e leads",
    navDescription: "Cadências automáticas para não perder oportunidade",
    seoTitle: "Automação de Follow-up e Acompanhamento de Leads | SVG Multimídia",
    seoDescription:
      "Implemente cadências automáticas de follow-up e pare de perder vendas por falta de retorno. Acompanhamento de leads com lembretes, mensagens e indicadores.",
    page: {
      eyebrow: "Follow-up",
      breadcrumb: "Automação de follow-up",
      h1: "Automação de follow-up e acompanhamento de leads para não perder venda por esquecimento",
      heroDescription:
        "Cadências programadas, lembretes automáticos e mensagens no tempo certo para reativar quem sumiu e converter quem estava só pensando.",
      problemTitle: "A maioria das vendas acontece depois do quinto contato",
      problemText:
        "E a maioria das empresas para no primeiro. O lead pede orçamento, some por alguns dias, ninguém retorna — e a oportunidade morre em silêncio, mesmo tendo custado dinheiro para ser gerada.",
      whatTitle: "Como automatizamos o acompanhamento",
      whatItems: [
        { k: "followup", c: "Funil", t: "Cadências por etapa", d: "Sequências de retorno específicas para orçamento enviado, sem resposta e reativação." },
        { k: "reminder", c: "Gestão", t: "Lembretes ao vendedor", d: "Tarefas automáticas para contatos que exigem toque humano." },
        { k: "broadcast", c: "Integrações", t: "Reativação de base", d: "Campanhas para clientes antigos e oportunidades perdidas." },
        { k: "lead", c: "Funil", t: "Score de interesse", d: "Priorização dos leads mais quentes com base em comportamento." },
        { k: "notify", c: "Atendimento", t: "Alertas de inatividade", d: "Avisos quando uma oportunidade fica parada além do prazo." },
        { k: "metrics", c: "Resultado", t: "Medição de recuperação", d: "Quantas vendas vieram de follow-up automatizado." },
      ],
      benefits: [
        "Recuperação de oportunidades já pagas",
        "Nenhum orçamento sem retorno",
        "Vendedor sabe exatamente quem chamar hoje",
        "Reativação de clientes antigos",
        "Aumento de conversão sem aumentar investimento",
        "Ciclo de venda mais curto",
      ],
      keywords: ["automação de follow-up", "acompanhamento de leads", "cadência de vendas", "reativação de leads"],
      serviceName: "Automação de Follow-up e Acompanhamento de Leads",
      serviceDescription:
        "Criação e implantação de cadências automáticas de follow-up, lembretes de tarefa, campanhas de reativação e indicadores de recuperação de oportunidades.",
      introParagraphs: [
        "Follow-up é a etapa mais negligenciada e mais lucrativa do processo comercial. O lead já demonstrou interesse, já custou investimento e já conhece sua empresa — falta apenas persistência organizada.",
        "Montamos cadências que combinam mensagens automáticas e toques humanos, com intervalos e conteúdos definidos por etapa. O vendedor recebe a tarefa certa no dia certo e o sistema cuida do restante.",
      ],
      metrics: [
        { value: "5x", label: "contatos médios até o fechamento" },
        { value: "+30%", label: "de oportunidades recuperadas" },
        { value: "0", label: "orçamentos sem retorno" },
        { value: "Auto", label: "reativação de base antiga" },
      ],
      differentials: [
        { t: "Cadência com conteúdo", d: "Não é só 'e aí, pensou?'. Cada toque leva um motivo, um argumento ou uma prova." },
        { t: "Híbrido humano + automático", d: "O sistema executa o repetitivo e sinaliza quando o contato precisa ser pessoal." },
        { t: "Reativação de base", d: "Trabalhamos também o histórico de leads perdidos, um ativo que quase ninguém usa." },
      ],
      forWho: [
        "Empresas que enviam muitos orçamentos e fecham poucos",
        "Negócios com ciclo de venda de dias ou semanas",
        "Times que esquecem de retornar contatos",
        "Operações com grande base de leads antigos",
        "Empresas que investem em anúncios e não aproveitam tudo",
      ],
      deliverables: [
        "Mapeamento dos pontos de perda no funil",
        "Desenho das cadências por etapa",
        "Redação das mensagens de follow-up",
        "Configuração de automações e lembretes",
        "Campanha de reativação da base",
        "Painel de recuperação de oportunidades",
      ],
      faq: [
        { q: "Não vou incomodar o cliente?", a: "As cadências têm limite, intervalo e conteúdo relevante, além de saída fácil para quem não tem interesse." },
        { q: "Funciona por WhatsApp?", a: "Sim, além de e-mail e tarefas internas para contato telefônico." },
        { q: "Consigo medir o resultado?", a: "Sim. Separamos as vendas originadas de follow-up automatizado nos relatórios." },
      ],
    },
  }),
  build({
    slug: "diagnostico-de-atendimento-e-vendas",
    navLabel: "Diagnóstico de atendimento e vendas",
    navDescription: "Mapeamento dos gargalos que travam sua operação",
    seoTitle: "Diagnóstico de Atendimento e Vendas | SVG Multimídia",
    seoDescription:
      "Descubra onde sua empresa perde oportunidades. Diagnóstico completo de atendimento e vendas com análise de canais, tempo de resposta, funil e plano de ação.",
    page: {
      eyebrow: "Diagnóstico",
      breadcrumb: "Diagnóstico de atendimento e vendas",
      h1: "Diagnóstico de atendimento e vendas: descubra exatamente onde sua empresa está perdendo dinheiro",
      heroDescription:
        "Análise objetiva de canais, tempo de resposta, qualidade das conversas e conversão por etapa, com plano de ação priorizado.",
      problemTitle: "Você não pode corrigir o que não consegue enxergar",
      problemText:
        "Muitas empresas sentem que 'algo não vai bem' no comercial, mas não sabem apontar onde. Sem medir tempo de resposta, taxa de conversão e motivos de perda, qualquer decisão vira aposta.",
      whatTitle: "O que analisamos no diagnóstico",
      whatItems: [
        { k: "analysis", c: "Estratégia", t: "Auditoria de conversas", d: "Leitura de atendimentos reais para avaliar abordagem, ritmo e clareza." },
        { k: "metrics", c: "Resultado", t: "Tempo de resposta", d: "Medição do intervalo entre o contato do cliente e o primeiro retorno." },
        { k: "funnel", c: "Funil", t: "Conversão por etapa", d: "Onde os leads travam e qual estágio derruba mais oportunidades." },
        { k: "multi", c: "Integrações", t: "Mapa de canais", d: "Volume, qualidade e custo por canal de entrada de contatos." },
        { k: "lead", c: "Funil", t: "Qualidade do lead", d: "Se o problema é volume, perfil ou tratamento das oportunidades." },
        { k: "dashboard", c: "Gestão", t: "Plano de ação", d: "Prioridades objetivas com impacto estimado e esforço de execução." },
      ],
      benefits: [
        "Clareza sobre os reais gargalos",
        "Decisões baseadas em dados, não em achismo",
        "Ganhos rápidos identificados logo no início",
        "Base para investir no que traz retorno",
        "Alinhamento entre marketing e vendas",
        "Plano de ação pronto para executar",
      ],
      keywords: ["diagnóstico comercial", "auditoria de atendimento", "análise de vendas", "consultoria comercial"],
      serviceName: "Diagnóstico de Atendimento e Vendas",
      serviceDescription:
        "Análise completa da operação de atendimento e vendas: auditoria de conversas, medição de tempo de resposta, conversão por etapa, mapa de canais e plano de ação priorizado.",
      introParagraphs: [
        "O diagnóstico é o ponto de partida de qualquer melhoria comercial. Em poucas semanas, ele mostra com números onde as oportunidades se perdem: na origem, no primeiro atendimento, na proposta ou no follow-up.",
        "É um trabalho de campo: lemos conversas reais, medimos tempos, entrevistamos a equipe e comparamos com o que o mercado espera. A entrega é um relatório objetivo, com prioridades e estimativa de impacto.",
      ],
      metrics: [
        { value: "Dados", label: "decisões baseadas em números reais" },
        { value: "Ranking", label: "gargalos priorizados por impacto" },
        { value: "Quick wins", label: "ganhos rápidos identificados" },
        { value: "Plano", label: "ações com prazo e responsável" },
      ],
      differentials: [
        { t: "Conversas reais, não hipóteses", d: "Analisamos o atendimento como ele acontece, não como deveria acontecer." },
        { t: "Visão de origem à venda", d: "Consideramos SEO, anúncios e canais orgânicos como parte do diagnóstico." },
        { t: "Entrega acionável", d: "Você recebe prioridades executáveis, não um relatório para engavetar." },
      ],
      forWho: [
        "Empresas com queda ou estagnação de vendas",
        "Gestores sem indicadores comerciais",
        "Negócios que investem em marketing sem retorno claro",
        "Operações antes de contratar automação ou CRM",
        "Empresas em processo de reestruturação comercial",
      ],
      deliverables: [
        "Auditoria de atendimentos reais",
        "Relatório de tempo de resposta por canal",
        "Análise de conversão por etapa do funil",
        "Mapa de canais e origem dos contatos",
        "Lista priorizada de gargalos",
        "Plano de ação com prazos e responsáveis",
      ],
      faq: [
        { q: "Quanto tempo leva?", a: "Normalmente entre 1 e 3 semanas, dependendo do volume de conversas e canais analisados." },
        { q: "Preciso liberar acesso a dados sensíveis?", a: "Trabalhamos com amostras de conversas e indicadores, sempre com acordo de confidencialidade." },
        { q: "E depois do diagnóstico?", a: "Você pode executar internamente ou contratar a implantação com a SVG. O plano é seu de qualquer forma." },
      ],
    },
  }),
  build({
    slug: "automacao-comercial-para-grandes-empresas",
    navLabel: "Automação para grandes empresas",
    navDescription: "Multi-equipe, governança e integrações corporativas",
    seoTitle: "Automação Comercial para Grandes Empresas | SVG Multimídia",
    seoDescription:
      "Automação comercial para operações de grande porte: múltiplas equipes, filiais, governança de dados, integrações corporativas e indicadores consolidados.",
    page: {
      eyebrow: "Enterprise",
      breadcrumb: "Automação para grandes empresas",
      h1: "Automação comercial para grandes empresas com múltiplas equipes, filiais e alto volume de operação",
      heroDescription:
        "Padronização de atendimento, governança de dados, integrações com sistemas internos e visão consolidada de toda a operação comercial.",
      problemTitle: "Em operações grandes, o problema deixa de ser velocidade e passa a ser controle",
      problemText:
        "Filiais com processos diferentes, times que não compartilham informação, relatórios que não batem e um custo de atendimento que cresce mais rápido que a receita. Escala sem padronização vira caos caro.",
      whatTitle: "O que endereçamos em operações de grande porte",
      whatItems: [
        { k: "agents", c: "Gestão", t: "Multi-equipe e filiais", d: "Estruturas separadas por unidade com padrão comum e visão consolidada." },
        { k: "api", c: "Integrações", t: "Integrações corporativas", d: "Conexão com ERP, sistemas internos e ferramentas já utilizadas." },
        { k: "meta", c: "Gestão", t: "Governança e permissões", d: "Perfis de acesso, auditoria de conversas e controle de dados." },
        { k: "ai-wa", c: "IA", t: "IA em escala", d: "Atendimento automatizado dimensionado para milhares de contatos simultâneos." },
        { k: "dashboard", c: "Resultado", t: "BI comercial", d: "Painéis consolidados por unidade, equipe, produto e canal." },
        { k: "analysis", c: "Estratégia", t: "Gestão de mudança", d: "Plano de adoção, treinamento e acompanhamento por fases." },
      ],
      benefits: [
        "Padrão único de atendimento entre unidades",
        "Visão consolidada da operação comercial",
        "Custo de atendimento sob controle na escala",
        "Conformidade e rastreabilidade das conversas",
        "Integração com o ecossistema de sistemas existente",
        "Implantação por fases, sem parar a operação",
      ],
      keywords: ["automação comercial enterprise", "automação para grandes empresas", "CRM corporativo", "governança comercial"],
      serviceName: "Automação Comercial para Grandes Empresas",
      serviceDescription:
        "Projeto corporativo de automação comercial com múltiplas equipes e filiais, governança de acessos, integrações com sistemas internos, IA em escala e BI consolidado.",
      introParagraphs: [
        "Empresas de grande porte não sofrem por falta de ferramentas — sofrem por excesso delas sem integração. Cada área tem seu sistema, cada filial tem seu jeito e a diretoria não consegue uma visão única e confiável.",
        "Nosso trabalho nesse cenário é de arquitetura: definir o padrão de processo, escolher onde cada informação vive, integrar os sistemas existentes e implantar por fases, com gestão de mudança para garantir adoção.",
      ],
      metrics: [
        { value: "Multi", label: "unidades e equipes em um padrão" },
        { value: "API", label: "integração com sistemas internos" },
        { value: "Fases", label: "implantação sem parar a operação" },
        { value: "BI", label: "indicadores consolidados" },
      ],
      differentials: [
        { t: "Arquitetura antes de ferramenta", d: "Definimos o modelo de dados e o fluxo antes de qualquer configuração." },
        { t: "Implantação por ondas", d: "Piloto em uma unidade, ajuste e expansão controlada para as demais." },
        { t: "Gestão de mudança", d: "Treinamento, materiais e acompanhamento de adoção por equipe." },
        { t: "Governança de dados", d: "Perfis, permissões e auditoria para operações que exigem controle." },
      ],
      forWho: [
        "Empresas com múltiplas filiais ou unidades",
        "Operações com dezenas de atendentes",
        "Redes e franquias que precisam padronizar",
        "Negócios com ERP e sistemas legados",
        "Diretorias que precisam de visão consolidada",
      ],
      deliverables: [
        "Arquitetura de processo e dados",
        "Plano de integrações com sistemas internos",
        "Modelo de governança e permissões",
        "Piloto em unidade e plano de expansão",
        "Configuração de IA em escala",
        "Painéis de BI consolidados",
        "Programa de treinamento e adoção",
      ],
      faq: [
        { q: "Integra com nosso ERP?", a: "Avaliamos as APIs disponíveis e desenhamos a integração viável para o seu cenário." },
        { q: "Como é feita a implantação?", a: "Por fases, começando com um piloto controlado e expandindo após validação." },
        { q: "Vocês atendem operações de todo o Brasil?", a: "Sim. O trabalho é conduzido remotamente, com encontros presenciais quando necessário." },
      ],
    },
  }),
  build({
    slug: "atendimento-para-alto-volume-de-contatos",
    navLabel: "Atendimento para alto volume",
    navDescription: "Escala sem perder qualidade nem tempo de resposta",
    seoTitle: "Soluções de Atendimento para Empresas com Alto Volume de Contatos | SVG Multimídia",
    seoDescription:
      "Estruture o atendimento para grandes volumes: IA de triagem, filas inteligentes, distribuição automática e indicadores de nível de serviço em tempo real.",
    page: {
      eyebrow: "Alto Volume",
      breadcrumb: "Atendimento para alto volume",
      h1: "Soluções de atendimento para empresas com alto volume de contatos todos os dias",
      heroDescription:
        "Triagem com IA, filas inteligentes e distribuição automática para manter tempo de resposta baixo mesmo em picos de demanda.",
      problemTitle: "Quando o volume cresce, a qualidade costuma cair junto",
      problemText:
        "Campanhas, sazonalidade e crescimento trazem picos de mensagens. Sem uma estrutura preparada, o tempo de resposta dispara, mensagens ficam sem retorno e a equipe entra em modo apagar incêndio.",
      whatTitle: "Como preparamos a operação para escala",
      whatItems: [
        { k: "ai-wa", c: "IA", t: "Primeira linha com IA", d: "A IA resolve o simples e filtra o que realmente precisa de atendente." },
        { k: "agents", c: "Atendimento", t: "Filas e distribuição", d: "Roteamento automático por assunto, prioridade e disponibilidade." },
        { k: "tags", c: "Gestão", t: "Classificação automática", d: "Cada contato é categorizado para relatórios e priorização." },
        { k: "notify", c: "Gestão", t: "Controle de SLA", d: "Alertas em tempo real quando o tempo de resposta ultrapassa o limite." },
        { k: "broadcast", c: "Integrações", t: "Comunicação em massa", d: "Avisos e campanhas segmentadas sem travar o atendimento individual." },
        { k: "dashboard", c: "Resultado", t: "Monitoramento em tempo real", d: "Painel de volume, fila, tempo médio e produtividade por atendente." },
      ],
      benefits: [
        "Tempo de resposta estável mesmo em picos",
        "Equipe dimensionada com base em dados",
        "Menos abandono de conversa",
        "Distribuição justa da carga de trabalho",
        "Relatórios confiáveis de demanda",
        "Custo por atendimento reduzido",
      ],
      keywords: ["atendimento alto volume", "central de atendimento", "filas de atendimento", "SLA de atendimento"],
      serviceName: "Soluções de Atendimento para Alto Volume de Contatos",
      serviceDescription:
        "Estruturação de atendimento para grandes volumes com IA de primeira linha, filas inteligentes, distribuição automática, controle de SLA e monitoramento em tempo real.",
      introParagraphs: [
        "Alto volume exige um desenho diferente: nem toda mensagem precisa de uma pessoa, e nem todo atendente deve receber qualquer assunto. A chave é filtrar, classificar e distribuir antes que a fila se forme.",
        "Implantamos uma primeira linha de IA que absorve dúvidas frequentes, coleta dados iniciais e encaminha apenas o que exige julgamento humano — com filas, prioridades e monitoramento de SLA em tempo real.",
      ],
      metrics: [
        { value: "Picos", label: "absorvidos sem aumentar o time" },
        { value: "SLA", label: "tempo de resposta monitorado" },
        { value: "Filas", label: "distribuição automática por assunto" },
        { value: "Tempo real", label: "painel de volume e produtividade" },
      ],
      differentials: [
        { t: "Dimensionamento por dados", d: "Analisamos o histórico de volume por horário e dia para definir a estrutura ideal." },
        { t: "IA como primeira linha", d: "Boa parte dos contatos é resolvida antes de ocupar um atendente." },
        { t: "Monitoramento ativo", d: "Alertas de fila e SLA permitem reagir antes que o cliente reclame." },
      ],
      forWho: [
        "Empresas com centenas ou milhares de contatos por dia",
        "Operações com forte sazonalidade",
        "Negócios que fazem campanhas com grande resposta",
        "Centrais de atendimento e suporte",
        "Redes com atendimento centralizado",
      ],
      deliverables: [
        "Análise de volume e sazonalidade",
        "Configuração da IA de primeira linha",
        "Estrutura de filas e regras de roteamento",
        "Definição e monitoramento de SLAs",
        "Painel de acompanhamento em tempo real",
        "Treinamento e rotina de gestão da operação",
      ],
      faq: [
        { q: "Quanto do volume a IA consegue absorver?", a: "Depende do perfil das mensagens; em operações com muitas dúvidas repetidas, é comum a IA resolver a maior parte do primeiro contato." },
        { q: "Funciona em picos sazonais?", a: "Sim. A estrutura é dimensionada considerando os períodos de maior demanda." },
        { q: "Consigo acompanhar em tempo real?", a: "Sim, com painel de fila, tempo médio de resposta e produtividade por atendente." },
      ],
    },
  }),
];

export const AUTOMACAO_LIST = list;
export const AUTOMACAO_PAGES: Record<string, AutomacaoPage> = Object.fromEntries(
  list.map((p) => [p.slug, p]),
);
